import { ELanguage, EProductVariantType } from '@ringana/configs';
import {
  IApiBooklet,
  IApiExtendedSingleProductData,
  IApiMediaPost,
  IApiPreviewBlogPost,
  IApiRethinkArticle,
  IApiSingleProductData,
  IApiSliderBlogPost,
} from '@/common/interfaces/api.interface';
import { IMediaPost } from '@/common/interfaces/media.interface';
import { IArticleCard } from '@/common/interfaces/article-card.interface';
import { IBooklet } from '@/common/interfaces/magazine.interface';
import { IPreviewBlogPost, ISliderBlogPost } from '@/common/interfaces/ringana-blog.interfaces';
import { IExtendedSingleProductData, ISingleProductData } from '../common/interfaces/products.interface';

export default class WpApiHelper {
  public static transformIApiMediaPostToIMediaPost(data: IApiMediaPost): IMediaPost {
    return {
      cover: data.cover ?? ``,
      date: data.date ?? ``,
      isDownload: !!data.download ?? false,
      link: (data.download || data.link) ?? ``,
      title: data.title ?? ``,
    };
  }

  public static transformIApiMediaPostsToIMediaPosts(data: IApiMediaPost[]): IMediaPost[] {
    return data.map(post => this.transformIApiMediaPostToIMediaPost(post));
  }

  public static transformIApiRethinkArticleToIArticleCard(data: IApiRethinkArticle): IArticleCard {
    return {
      backgroundImageLink: data?.articleImage ? `${data.articleImage}?w=367` : ``,
      hoverImageLink: data?.articleOverlayImage ? `${data.articleOverlayImage}?w=367` : ``,
      linkToArticle: data?.articleUrl ?? ``,
      text: data?.articleText ?? ``,
      title: data?.articleTitle ?? ``,
    };
  }

  public static transformIApiRethinkArticlesToIArticleCards(data: IApiRethinkArticle[]): IArticleCard[] {
    return data.map(item => this.transformIApiRethinkArticleToIArticleCard(item));
  }

  public static transformIApiBookletToIBooklet(data: IApiBooklet): IBooklet {
    return {
      edition: data?.edition ?? ``,
      issueCode: data?.issueCode ?? ``,
      shortDescription: data?.shortDescription ?? ``,
      title: data?.title ?? ``,
    };
  }

  public static transformIApiBookletsToIBooklets(data: IApiBooklet[]): IBooklet[] {
    return data.map(item => this.transformIApiBookletToIBooklet(item));
  }

  public static transformIApiSliderBlogPostToISliderBlogPost(data: IApiSliderBlogPost): ISliderBlogPost {
    return {
      backgroundImg: data?.blogPostSliderImg ?? ``,
      link: data?.blogPostPermalink ?? ``,
      subtext: data?.blogPostSubheadline ?? ``,
      title: data?.blogPostTitle ?? ``,
    };
  }

  public static transformIApiSliderBlogPostsToISliderBlogPosts(data: IApiSliderBlogPost[]): ISliderBlogPost[] {
    return data.map(item => this.transformIApiSliderBlogPostToISliderBlogPost(item));
  }

  public static transformIApiPreviewBlogPostToIPreviewBlogPost(data: IApiPreviewBlogPost): IPreviewBlogPost {
    return {
      link: data?.blogPostPermalink ?? ``,
      previewImg: data?.blogPostPreviewImg ?? ``,
      title: data?.blogPostTitle ?? ``,
    };
  }

  public static transformIApiPreviewBlogPostsToIPreviewBlogPosts(data: IApiPreviewBlogPost[]): IPreviewBlogPost[] {
    return data.map(item => this.transformIApiPreviewBlogPostToIPreviewBlogPost(item));
  }

  public static transformIApiSingleProductDataToISingleProductData(data: IApiSingleProductData, imagesBaseUrl: string): ISingleProductData {
    return {
      awards: {
        beatTheMicrobead: {
          image: `${imagesBaseUrl}beat-the-microbead.svg`,
          link: data.productAwardBeatTheMicrobeadLink,
          received: data.productAwardBeatTheMicrobead,
          translationName: `zeroProducts`,
        },
        codecheck: {
          image: `${imagesBaseUrl}label-codechecker.svg`,
          link: data.productAwardCodecheckLink,
          received: data.productAwardCodecheck,
          translationName: `codecheck`,
        },
        cosmeticAnalysis: {
          image: `${imagesBaseUrl}label-cosmeticanalysis.png`,
          link: data.productAwardCosmeticAnalysisLink,
          received: data.productAwardCosmeticAnalysis,
          translationName: `cosmeticAnalysis`,
        },
        cosmosNatural: {
          image: `${imagesBaseUrl}label-cosmos-natural.png`,
          link: data.productAwardCosmosNaturalLink,
          received: data.productAwardCosmosNatural,
          translationName: `cosmosNatural`,
        },
        cosmosOrganic: {
          image: `${imagesBaseUrl}label-cosmos-organic.png`,
          link: data.productAwardCosmosOrganicLink,
          received: data.productAwardCosmosOrganic,
          translationName: `cosmosOrganic`,
        },
        ecocert: {
          image: `${imagesBaseUrl}label-organic-cosmetic.png`,
          received: data.productAwardEcocert,
          translationName: `ecocert`,
        },
        ecocertNaturkosmetik: {
          image: `${imagesBaseUrl}label-natural-cosmetic.png`,
          received: data.productAwardEcocertNatural,
          translationName: `ecocertNaturkosmetik`,
        },
        euEcolabel: {
          image: `${imagesBaseUrl}eco-label.png`,
          link: data.productAwardEuEcolabelLink,
          received: data.productAwardEuEcolabel,
          translationName: `euEcolabel`,
        },
        koelner: {
          image: `${imagesBaseUrl}label-koelner-liste.png`,
          received: data.productAwardKoelnerListe,
          translationName: `koelner`,
        },
        lacon: {
          image: `${imagesBaseUrl}label-lacon.png`,
          link: data.productAwardLaconLink,
          received: data.productAwardLacon,
          translationName: `lacon`,
        },
        patentiert: {
          image: `${imagesBaseUrl}label-patentiert-de.png`,
          received: data.productAwardPatented,
          translationName: `patentiert`,
        },
        peta: {
          image: `${imagesBaseUrl}PETA_Approved.jpg`,
          link: data.productAwardPetaLink,
          received: data.productAwardPeta,
          translationName: `peta`,
        },
        positiveReef: {
          image: `${imagesBaseUrl}positive-reef.jpg`,
          link: data.productAwardPositiveReefLink,
          received: data.productAwardPositiveReef,
          translationName: `positiveReef`,
        },
        umweltzeichen: {
          image: `${imagesBaseUrl}label-umweltzeichen.png`,
          link: data.productAwardUmweltzeichenLink,
          received: data.productAwardUmweltzeichen,
          translationName: `umweltzeichen`,
        },
        uvaProtection: {
          image: `${imagesBaseUrl}label-UVA.svg`,
          received: data.productAwardUvaProtected,
          translationName: `uvaProtection`,
        },
        uvbB: {
          image: `${imagesBaseUrl}uvb-b.png`,
          link: data.productAwardUvbBLink,
          received: data.productAwardUvbB,
          translationName: `uvbB`,
        },
      },
      category: data.category,
      categoryDynamicText: ``,
      image: data.image,
      informationLinkColor: data.nemBackgroundColor,
      interestingArticles: [
        {
          external: data.articleTeaserOneExternal ?? ``,
          headline: data.articleTeaserOneHeadline ?? ``,
          image: data.articleTeaserOneImage ?? ``,
          link: data.articleTeaserOneLink ?? ``,
          text: data.articleTeaserOneText ?? ``,
        }, {
          external: data.articleTeaserTwoExternal ?? ``,
          headline: data.articleTeaserTwoHeadline ?? ``,
          image: data.articleTeaserTwoImage ?? ``,
          link: data.articleTeaserTwoLink ?? ``,
          text: data.articleTeaserTwoText ?? ``,
        }, {
          external: data.articleTeaserThreeExternal ?? ``,
          headline: data.articleTeaserThreeHeadline ?? ``,
          image: data.articleTeaserThreeImage ?? ``,
          link: data.articleTeaserThreeLink ?? ``,
          text: data.articleTeaserThreeText ?? ``,
        },
      ],
      matchcode: data.matchcode,
      productBannerData: {
        banner: data.productBanner,
        text: data.productBannerText,
        textColor: data.productBannerTextColor,
        textHeadline: data.productBannerTextHeadline,
        textLeft: data.productBannerTextLeft,
      },
      productType: +data.productType,
      productVideoData: {
        poster: data.productVideoPoster,
        video: data.productVideo,
        videoOgv: data.productVideoOgv,
        videoWebm: data.productVideoWebm,
      },
      shortDescription: data.shortInfo,
      shortDescriptionMail: ``,
      tabsData: {
        description: data.productDescription,
        howTo: data.productHowTo,
        image: data.image,
        ingredients: data.productIngredients,
        knowHow: data.productKnowHow,
        statutoryInfos: data.productStatutoryInfos,
        statutoryTable: data.productStatutoryTable,
      },
      title: data.title,
      type: data.productType,
      usp: data.usp,
      wpProductId: data.wordpressID?.toString() ?? ``,
    };
  }

  public static transformIApiExtendedSingleProductDataToIExtendedSingleProductData(data: IApiExtendedSingleProductData, imagesBaseUrl: string): IExtendedSingleProductData {
    const baseData = WpApiHelper.transformIApiSingleProductDataToISingleProductData(data, imagesBaseUrl);

    const notEmptyImages = [
      data.productImage1,
      data.productImage2,
      data.productImage3,
      data.productImage4,
      data.productImage5,
      data.productImage6,
    ].filter(image => image !== ``);

    const extendedData: IExtendedSingleProductData = {
      ...baseData,
      faqs: data.faqs,
      idealSupplements: data.idealSupplements,
      productImageAlternative: data.productImageAlternative,
      productImages: notEmptyImages,
      productVariants: [],
      teaserDots: [],
      unitPriceData: {
        amount: data.unitPriceAmount,
        type: data.unitPriceType,
      },
      values: data.values,
    };
    extendedData.teaserDots = data.teaserDots.map(dot => ({ ...dot, id: -1 }));

    extendedData.productVariants = data.productVariants.map(variant => ({
      color: variant.color && variant.name ? variant.color : null,
      id: variant.productId,
      language: variant.language as ELanguage ?? null,
      matchcode: data.matchcode,
      name: variant.color && variant.name ? variant.name : null,
      size: variant.size && variant.unit ? variant.size : null,
      slug: variant.slug,
      // eslint-disable-next-line no-nested-ternary
      type: variant.language
        ? EProductVariantType.LANGUAGE
        : variant.size && variant.unit
          ? EProductVariantType.SIZE
          : EProductVariantType.COLOR,
      unit: variant.size && variant.unit ? variant.unit : null,
    }));

    return extendedData;
  }
}
